import React, { useState, useEffect } from 'react';
import { Search, Bot, Loader2, AlertCircle, FileText, RefreshCw, Database, CheckSquare, Square } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useAuthStore } from '../../stores/authStore';

interface Chunk {
  id: string;
  texto: string;
  orden: number;
  extract_result_id: string;
  thread_id: string;
  created_at: string;
  embedding: boolean;
  processed: boolean;
  extract_result?: {
    url: string;
    asistente_id?: string;
    asistente?: {
      nombre: string;
      api_configuracion_id?: string;
      vector_table_id?: string;
    };
  };
}

interface TipoEmbedding {
  id: string;
  codigo: string;
  nombre: string;
  proveedor: 'openai' | 'cohere' | 'other';
  dimensiones: number;
  max_tokens: number;
  costo_token: number;
  estado: string;
}

interface APIConfig {
  id: string;
  api_key: string;
  organization_id: string | null;
}

interface ProgressInfo {
  total: number;
  processed: number;
  errors: number;
  currentChunk?: string;
}

export default function Chunks() {
  const { user } = useAuthStore();
  const [chunks, setChunks] = useState<Chunk[]>([]);
  const [tiposEmbedding, setTiposEmbedding] = useState<TipoEmbedding[]>([]);
  const [selectedEmbeddingType, setSelectedEmbeddingType] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedChunks, setSelectedChunks] = useState<Set<string>>(new Set());
  const [progress, setProgress] = useState<ProgressInfo | null>(null);

  useEffect(() => {
    fetchData();
    fetchTiposEmbedding();
  }, []);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      setError('');

      const { data: chunksData, error: chunksError } = await supabase
        .from('chunks')
        .select(`
          *,
          extract_result:tavily_extract_results(
            url,
            asistente_id,
            asistente:asistentes(
              nombre,
              api_configuracion_id,
              vector_table_id
            )
          )
        `)
        .eq('embedding', false)
        .order('created_at', { ascending: false });

      if (chunksError) throw chunksError;
      setChunks(chunksData || []);

    } catch (error: any) {
      console.error('Error fetching data:', error);
      setError('Error al cargar los datos');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchTiposEmbedding = async () => {
    try {
      const { data, error } = await supabase
        .from('tipos_embeddings')
        .select('*')
        .eq('estado', 'activo')
        .order('nombre');

      if (error) throw error;
      setTiposEmbedding(data || []);
      
      // Set default to text-embedding-3-small if available
      const defaultType = data?.find(t => t.codigo === 'text-embedding-3-small');
      if (defaultType) {
        setSelectedEmbeddingType(defaultType.id);
      } else if (data && data.length > 0) {
        setSelectedEmbeddingType(data[0].id);
      }
    } catch (error: any) {
      console.error('Error fetching embedding types:', error);
      setError('Error al cargar los tipos de embedding');
    }
  };

  const handleEmbedding = async () => {
    if (selectedChunks.size === 0) {
      setError('Selecciona al menos un chunk para procesar');
      return;
    }

    if (!selectedEmbeddingType) {
      setError('Selecciona un tipo de embedding');
      return;
    }

    try {
      setIsProcessing(true);
      setError('');

      // Get embedding type configuration
      const { data: embeddingType, error: typeError } = await supabase
        .from('tipos_embeddings')
        .select('*')
        .eq('id', selectedEmbeddingType)
        .single();

      if (typeError) throw typeError;
      if (!embeddingType) throw new Error('Tipo de embedding no encontrado');

      // Initialize progress
      setProgress({
        total: selectedChunks.size,
        processed: 0,
        errors: 0
      });

      for (const chunkId of selectedChunks) {
        const chunk = chunks.find(c => c.id === chunkId);
        if (!chunk || !chunk.extract_result?.asistente_id) continue;

        try {
          setProgress(prev => prev ? {
            ...prev,
            currentChunk: chunk.extract_result?.url
          } : null);

          // Get API configuration
          const { data: apiConfig, error: configError } = await supabase
            .from('api_configuraciones')
            .select('*')
            .eq('id', chunk.extract_result.asistente?.api_configuracion_id)
            .single();

          if (configError) throw configError;
          if (!apiConfig) throw new Error('No se encontró la configuración de API');

          // Generate embedding using OpenAI API
          const response = await fetch('https://api.openai.com/v1/embeddings', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${apiConfig.api_key}`,
              ...(apiConfig.organization_id && { 'OpenAI-Organization': apiConfig.organization_id })
            },
            body: JSON.stringify({
              input: chunk.texto,
              model: embeddingType.codigo
              // Remove dimensions parameter as it's not supported by the API
              // The dimensions are determined by the model
            })
          });

          if (!response.ok) {
            const error = await response.json();
            throw new Error(error.error?.message || 'Error al generar el embedding');
          }

          const data = await response.json();
          const embedding = data.data[0].embedding;

          // Verify embedding dimensions match the vector table
          if (embedding.length !== 1536) {
            throw new Error(`Dimensiones del embedding incorrectas: esperaba 1536, recibió ${embedding.length}`);
          }

          // Save to assistant's vector table
          if (chunk.extract_result.asistente?.vector_table_id) {
            const { error: insertError } = await supabase
              .from(chunk.extract_result.asistente.vector_table_id)
              .insert({
                document_id: chunk.extract_result_id,
                chunk_texto: chunk.texto,
                embedding: embedding,
                orden: chunk.orden,
                document_origin: embeddingType.proveedor
              });

            if (insertError) throw insertError;

            // Mark chunk as processed
            const { error: updateError } = await supabase
              .from('chunks')
              .update({ 
                processed: true,
                embedding: true 
              })
              .eq('id', chunk.id);

            if (updateError) throw updateError;

            // Remove chunk from local state
            setChunks(prev => prev.filter(c => c.id !== chunk.id));
          }

          setProgress(prev => prev ? {
            ...prev,
            processed: prev.processed + 1
          } : null);
          
          // Add a small delay between requests to avoid rate limiting
          await new Promise(resolve => setTimeout(resolve, 200));

        } catch (error: any) {
          console.error(`Error processing chunk ${chunkId}:`, error);
          setProgress(prev => prev ? {
            ...prev,
            errors: prev.errors + 1
          } : null);
        }
      }

      const finalProgress = progress;
      if (finalProgress) {
        setSuccess(`Proceso completado. ${finalProgress.processed} chunks procesados. ${finalProgress.errors} errores.`);
      }
      setSelectedChunks(new Set());

    } catch (error: any) {
      console.error('Error in batch processing:', error);
      setError(`Error en el procesamiento por lotes: ${error.message}`);
    } finally {
      setIsProcessing(false);
      setProgress(null);
    }
  };

  const toggleChunkSelection = (chunkId: string) => {
    setSelectedChunks(prev => {
      const next = new Set(prev);
      if (next.has(chunkId)) {
        next.delete(chunkId);
      } else {
        next.add(chunkId);
      }
      return next;
    });
  };

  const selectAllChunks = () => {
    setSelectedChunks(new Set(filteredChunks.map(chunk => chunk.id)));
  };

  const deselectAllChunks = () => {
    setSelectedChunks(new Set());
  };

  const filteredChunks = chunks.filter(chunk => {
    if (searchTerm) {
      return (
        chunk.texto.toLowerCase().includes(searchTerm.toLowerCase()) ||
        chunk.extract_result?.url.toLowerCase().includes(searchTerm.toLowerCase()) ||
        chunk.extract_result?.asistente?.nombre.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    return true;
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-800">Chunks</h1>
        <div className="flex items-center space-x-4">
          <select
            value={selectedEmbeddingType}
            onChange={(e) => setSelectedEmbeddingType(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md text-sm"
            disabled={isProcessing}
          >
            <option value="">Seleccionar tipo de embedding</option>
            {tiposEmbedding.map(tipo => (
              <option key={tipo.id} value={tipo.id}>
                {tipo.nombre} ({tipo.dimensiones}d)
              </option>
            ))}
          </select>

          <button
            onClick={handleEmbedding}
            disabled={isProcessing || selectedChunks.size === 0 || !selectedEmbeddingType}
            className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 flex items-center space-x-2"
          >
            {isProcessing ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                <span>Procesando...</span>
              </>
            ) : (
              <>
                <Database className="w-5 h-5" />
                <span>Crear Embeddings ({selectedChunks.size})</span>
              </>
            )}
          </button>

          <button
            onClick={fetchData}
            disabled={isProcessing}
            className="p-2 text-gray-600 hover:bg-gray-100 rounded-full"
            title="Actualizar resultados"
          >
            <RefreshCw className={`w-5 h-5 ${isProcessing ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded flex items-center">
          <AlertCircle className="w-5 h-5 mr-2" />
          {error}
        </div>
      )}

      {success && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded">
          {success}
        </div>
      )}

      {isProcessing && progress && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-medium text-blue-900">Procesando Embeddings</h3>
            <div className="text-sm text-blue-700">
              {progress.processed} / {progress.total} completados
            </div>
          </div>
          
          <div className="w-full bg-blue-200 rounded-full h-2.5 mb-2">
            <div 
              className="bg-blue-600 h-2.5 rounded-full transition-all duration-300"
              style={{ width: `${(progress.processed / progress.total) * 100}%` }}
            ></div>
          </div>

          {progress.currentChunk && (
            <div className="text-sm text-blue-700 truncate">
              Procesando: {progress.currentChunk}
            </div>
          )}

          {progress.errors > 0 && (
            <div className="text-sm text-red-600 mt-1">
              Errores: {progress.errors}
            </div>
          )}

          <div className="text-xs text-blue-600 mt-2">
            No cierres esta ventana hasta que el proceso haya terminado
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="relative flex-1">
            <input
              type="text"
              placeholder="Buscar chunks..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={isProcessing}
            />
            <Search className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
          </div>
          <div className="flex items-center space-x-2 ml-4">
            <button
              onClick={selectAllChunks}
              className="flex items-center px-3 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded-md"
              title="Seleccionar todos"
              disabled={isProcessing}
            >
              <CheckSquare className="w-4 h-4 mr-2" />
              Seleccionar todos
            </button>
            <button
              onClick={deselectAllChunks}
              className="flex items-center px-3 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded-md"
              title="Deseleccionar todos"
              disabled={isProcessing}
            >
              <Square className="w-4 h-4 mr-2" />
              Deseleccionar todos
            </button>
          </div>
        </div>

        <div className="space-y-4">
          {filteredChunks.map((chunk) => (
            <div key={chunk.id} className="border rounded-lg p-4">
              <div className="flex justify-between items-start mb-2">
                <div className="flex items-center space-x-4">
                  <input
                    type="checkbox"
                    checked={selectedChunks.has(chunk.id)}
                    onChange={() => toggleChunkSelection(chunk.id)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    disabled={isProcessing}
                  />
                  <div>
                    <a
                      href={chunk.extract_result?.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline font-semibold"
                    >
                      {chunk.extract_result?.url}
                    </a>
                    <div className="text-sm text-gray-500 mt-1">
                      <div className="flex items-center space-x-2">
                        <Bot className="w-4 h-4" />
                        <span>Asistente: {chunk.extract_result?.asistente?.nombre}</span>
                      </div>
                      <div className="mt-1">
                        Chunk #{chunk.orden + 1}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-2 text-gray-600 text-sm">
                <div className="max-h-32 overflow-y-auto">
                  {chunk.texto}
                </div>
              </div>

              <div className="text-xs text-gray-400 mt-2">
                Thread ID: {chunk.thread_id}
              </div>
            </div>
          ))}

          {filteredChunks.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No se encontraron chunks sin procesar</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}